/* eslint-disable max-lines-per-function */
import React, { useEffect } from "react";
import CssBaseline from "@mui/material/CssBaseline";
import Drawer, { DrawerProps } from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import { FormData } from "types/gql-types";
import { drawerWidth } from "components/header/layout/helpers";
import Header from "components/header";
import { humanizeFormType } from "utils/form-types";
import Section from "../section";
import { keyFromIdentity } from "../helpers";
import { QuestionProps } from "../question";
import FormSidebar from "./form-sidebar";
import FormNavigation from "./form-navigation";

import ValidationDebugger from "./validation-debugger";
import ValueDebugger from "./value-debugger";
import theme from "@/components/theme";

type Props = {
  window?: () => Window;
  children?: React.ReactNode;
  form: FormData;
  section?: string;
  path: { match?: { submissionId?: string } }
}

type WindowSize = {
  width: number;
  height: number;
}

const MOBILE_BREAKPOINT = theme.breakpoints.values.md;

// Hook
export const useWindowSize = (): WindowSize => {
  // Initialize state with undefined width/height so server and client renders match
  // Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/
  const [windowSize, setWindowSize] = React.useState<WindowSize>({
    width: 0,
    height: 0,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowSize;
};

const ResponsiveDrawer = (props: Props): React.ReactElement => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const [isMobile, setIsMobile] = React.useState(window.outerWidth <= MOBILE_BREAKPOINT);
  const size = useWindowSize();
  useEffect(() => setIsMobile(size.width <= MOBILE_BREAKPOINT), [size]);

  const { form: {
    layouts: [{ groups }],
    questions,
    property: { name: propertyName },
    type: formType,
  } } = props;

  const formTitle = `${propertyName} ${humanizeFormType(formType)}`;

  const [group] = groups;
  const section = props.section || group.name;

  const drawerProps = (isMobile ? {
    variant: "temporary",
    open: mobileOpen,
    onClose: handleDrawerToggle,
    // Better open performance on mobile.
    ModalProps: { keepMounted: true },
  } : {}) as DrawerProps;

  const questionsMap = questions
    .reduce((acc: Record<string, QuestionProps>, question: QuestionProps) =>
      ({ ...acc, [keyFromIdentity(question.identity)]: question }), {});

  const mobileMenu = { toggle: handleDrawerToggle, isMobile };

  return (
    <div>
      <CssBaseline />
      <Header short />
      <Box component="nav" aria-label="mailbox folders">
        <Drawer
          open
          variant="permanent"
          sx={{ width: `${drawerWidth}px` }}
          {...drawerProps}
        >
          <FormSidebar groups={groups} section={section} path={props.path}
            questionsMap={questionsMap} mobileMenu={mobileMenu} />
        </Drawer>
      </Box>
      <Box component="main" sx={{
        flexGrow: 1,
        padding: 3,
        marginLeft: isMobile ? "0" : `${drawerWidth}px`,
      }}>
        <Section
          groups={groups}
          section={section}
          questionsMap={questionsMap}
          mobileMenu={mobileMenu}
          formTitle={formTitle}
        >
          {props.children}
          <FormNavigation groups={groups} form={props.form}
            section={props.section} path={props.path} mobileMenu={mobileMenu} />
          <ValidationDebugger />
          <ValueDebugger />
        </Section>
      </Box>
    </div>
  );
};

export default ResponsiveDrawer;
